import React from 'react';
import { saBillingFormatError, saBillingMapGlobalInvoice, saBillingMapGlobalSubscription } from './billing/billing-support.jsx';
import { AUDIT, PLANS, STORES, buildMockTenantDomain, setPLANS } from './data.jsx';
import { RESERVED_SUBDOMAINS } from '../src/new-store-form.js';
import {
  ALERT_RULES_MOCK,
  BILLING_OVERVIEW_MOCK,
  DUNNING_RULES_MOCK,
  FINANCIAL_HISTORY_MOCK,
  INTERNAL_USERS_EXT,
  INVOICES_MOCK,
  OPERATIONAL_NOTIFICATIONS_MOCK,
  _mockNotifStore,
  SA_ROLES_MOCK,
  SESSIONS_MOCK,
  STATUS_HISTORY_MOCK,
  SUBSCRIPTIONS_MOCK,
} from './extended-mock-data.jsx';
import { Icon } from './icons.jsx';
import {
  USE_MOCK_BILLING,
  USE_MOCK_REPAIRS,
  USE_MOCK_TENANTS,
  USE_REAL_BILLING_API as MVP_USE_REAL_BILLING_API,
  USE_REAL_NOTIFICATIONS_API as MVP_USE_REAL_NOTIFICATIONS_API,
  USE_REAL_REPAIRS_API as MVP_USE_REAL_REPAIRS_API,
  USE_REAL_TENANTS_API as MVP_USE_REAL_TENANTS_API,
  saIsRealBillingMode,
  saIsRealNotificationsMode,
  saIsRealRepairsMode,
  saIsRealTenantsMode,
  saShouldUseMockBilling,
  saShouldUseMockRepairs,
  saShouldUseMockTenants,
} from './mvp-config.jsx';
// ── API Service Layer — Super Admin Tezus ─────────────────────────────────────
// Camada mockável pronta para integração com backend Django.
//
// Para trocar mock por API real:
//   USE_MOCK_API = false
//   window.API_BASE_URL = 'https://manage1.tezus.com.br'
//
// Padrão de troca em cada método:
//   if (!USE_MOCK_API) { return apiClient.get('/api/v1/...'); }
//   await delay(NNN); return { data: MOCK_DATA };

// ── Configuração ──────────────────────────────────────────────────────────────
const isManageHost = window.location.hostname === 'manage.localhost';
const isLocalSuperAdminUI = ['localhost', '127.0.0.1'].includes(window.location.hostname);
// Empty string = same-origin (Vite proxies /api → manage). Do not treat '' as missing.
const API_BASE_URL = typeof window.API_BASE_URL === 'string'
  ? window.API_BASE_URL
  : (
    isManageHost
      ? window.location.origin
      : isLocalSuperAdminUI
        ? ''
        : 'https://manage1.tezus.com.br'
  );
const USE_MOCK_API = window.USE_MOCK_API !== false; // default: true (módulos fora da Fase 3)
const USE_REAL_TENANTS_API = MVP_USE_REAL_TENANTS_API !== false;
const USE_REAL_PLANS_API = window.USE_REAL_PLANS_API !== false;
const USE_REAL_BILLING_API = saIsRealBillingMode();
const USE_REAL_AUDIT_API = window.USE_REAL_AUDIT_API !== false;
const USE_REAL_INTERNAL_USERS_API = window.USE_REAL_INTERNAL_USERS_API !== false;
const USE_REAL_REPAIRS_API = saIsRealRepairsMode();
const USE_REAL_NOTIFICATIONS_API = saIsRealNotificationsMode();
const AUTH_STORAGE_KEY = 'tezus-sa-auth';
let __sa_token = '';
let __sa_refresh = '';
let __sa_user = null;
let __SA_PERMISSION_SCHEMA_VERSION__ = '1.31';

const isRealTenantsMode = () => saIsRealTenantsMode();
const shouldUseMockTenants = () => saShouldUseMockTenants();
const shouldUseMockBilling = () => saShouldUseMockBilling();
const isRealBillingMode = () => !shouldUseMockBilling();
const isRealRepairsMode = () => saIsRealRepairsMode();
const shouldUseMockRepairs = () => saShouldUseMockRepairs();

// ── Helpers ───────────────────────────────────────────────────────────────────
const delay = (ms) => new Promise(r => setTimeout(r, ms));

class ApiError extends Error {
  constructor(message, status = 0, payload = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

const formatTenantDomainUpdateError = (err) => {
  if (!(err instanceof ApiError)) return err?.message || 'Erro ao atualizar domínio.';
  const payload = err.payload || {};
  const detail = payload.detail || payload.error || err.message;
  if (err.status === 409) return 'Este domínio já está em uso por outra loja.';
  if (err.status === 404) return 'Tenant não encontrado.';
  if (err.status === 422) return detail || 'Não foi possível atualizar o domínio neste ambiente.';
  if (err.status === 400) return detail || 'Subdomínio inválido.';
  if (err.status === 0) return err.message;
  return detail || err.message;
};

const formatBillingStatusError = (err) => {
  if (!(err instanceof ApiError)) return err?.message || 'Erro ao alterar status comercial.';
  const payload = err.payload || {};
  const detail = payload.detail || payload.error || err.message;
  if (err.status === 404) return 'Tenant não encontrado.';
  if (err.status === 400) return detail || 'Status ou motivo inválido.';
  if (err.status === 0) return err.message;
  return detail || err.message;
};

const formatOperationalStatusError = (err) => {
  if (!(err instanceof ApiError)) return err?.message || 'Erro ao alterar status operacional.';
  const payload = err.payload || {};
  const detail = payload.detail || payload.error || err.message;
  if (err.status === 404) return 'Tenant não encontrado.';
  if (err.status === 400) return detail || 'Motivo inválido ou transição não permitida.';
  if (err.status === 0) return err.message;
  return detail || err.message;
};

const commercialStatusToUi = (status) => {
  const map = {
    past_due: 'overdue',
    active: 'active',
    trial: 'trial',
    suspended: 'suspended',
    cancelled: 'cancelled',
  };
  return map[status] || status;
};

const uiStatusToCommercial = (status) => {
  const map = {
    overdue: 'past_due',
    inadimplente: 'past_due',
    ativo: 'active',
    active: 'active',
    trial: 'trial',
    suspended: 'suspended',
    suspenso: 'suspended',
    cancelled: 'cancelled',
    cancelado: 'cancelled',
    past_due: 'past_due',
  };
  return map[status] || status;
};

const formatPlanUpdateError = (err) => {
  if (!(err instanceof ApiError)) return err?.message || 'Erro ao alterar plano.';
  const payload = err.payload || {};
  const detail = payload.detail || payload.error || err.message;
  if (err.status === 404) return 'Tenant não encontrado.';
  if (err.status === 422) return detail || 'Não foi possível sincronizar permissões neste tenant.';
  if (err.status === 400) return detail || 'Plano ou motivo inválido.';
  if (err.status === 0) return err.message;
  return detail || err.message;
};

const ALLOWED_PROFILE_STORE_SETTINGS_FIELDS = [
  'name', 'cnpj', 'phone', 'contact_email', 'street', 'number', 'complement',
  'neighborhood', 'city', 'state', 'postal_code',
  'notification_sender_email', 'notification_sender_name',
];

const normalizeStoreSettingsForProfile = (ss = {}) => ({
  name: ss.name || '',
  cnpj: ss.cnpj || '',
  phone: ss.phone || '',
  contact_email: ss.contact_email || '',
  street: ss.street || ss.address || '',
  number: ss.number || ss.address_number || '',
  complement: ss.complement || ss.address_complement || '',
  neighborhood: ss.neighborhood || '',
  city: ss.city || '',
  state: ss.state || '',
  postal_code: ss.postal_code || ss.postcode || '',
  notification_sender_email: ss.notification_sender_email || '',
  notification_sender_name: ss.notification_sender_name || '',
});

const normalizeProfileValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const buildTenantProfileUpdatePayload = ({ form, baseline, reason }) => {
  const payload = { reason: (reason || '').trim() };
  if (normalizeProfileValue(form.name) !== normalizeProfileValue(baseline.name)) {
    payload.name = form.name;
  }
  if (normalizeProfileValue(form.fiscal_name) !== normalizeProfileValue(baseline.fiscal_name)) {
    payload.fiscal_name = form.fiscal_name;
  }

  const storeSettings = {};
  const baseSs = baseline.store_settings || {};
  ALLOWED_PROFILE_STORE_SETTINGS_FIELDS.forEach((field) => {
    const formVal = form.store_settings?.[field] ?? form[field];
    const baseVal = baseSs[field];
    if (normalizeProfileValue(formVal) !== normalizeProfileValue(baseVal)) {
      storeSettings[field] = formVal ?? '';
    }
  });
  if (Object.keys(storeSettings).length) payload.store_settings = storeSettings;

  return payload;
};

const formatTenantProfileUpdateError = (err) => {
  if (!(err instanceof ApiError)) return err?.message || 'Erro ao atualizar dados cadastrais da loja.';
  const payload = err.payload || {};
  const detail = payload.detail || payload.error || err.message;
  if (err.status === 404) return 'Tenant não encontrado.';
  if (err.status === 422) {
    if (payload.code === 'missing_store_settings') {
      return 'Não foi possível atualizar: o tenant não possui configurações de loja no schema.';
    }
    if (payload.code === 'tenant_schema_inaccessible') {
      return 'Não foi possível acessar o schema do tenant para atualizar os dados da loja.';
    }
    return detail || 'Não foi possível atualizar os dados cadastrais neste ambiente.';
  }
  if (err.status === 400) return detail || 'Motivo inválido ou campo não permitido neste endpoint.';
  if (err.status === 0) return err.message;
  return detail || err.message;
};

const formatTenantCreateError = (err) => {
  if (!(err instanceof ApiError)) return err?.message || 'Erro inesperado ao criar tenant.';
  const payload = err.payload || {};
  const detail = payload.detail || payload.error || err.message;
  if (err.status === 422) {
    if (payload.code === 'missing_default_theme') {
      return 'Não foi possível criar a loja porque o ambiente está incompleto: tema padrão não encontrado.';
    }
    return `Não foi possível criar a loja porque o ambiente está incompleto: ${detail}`;
  }
  if (err.status === 409) {
    const conflicts = Array.isArray(payload.conflicts) ? payload.conflicts : [];
    if (conflicts.includes('email') && conflicts.includes('domain')) {
      return 'Este domínio e este e-mail de administrador já estão em uso.';
    }
    if (payload.code === 'duplicate_email') return 'Já existe uma loja com este e-mail de administrador.';
    if (payload.code === 'duplicate_domain') return 'Este domínio já está em uso por outra loja.';
    if (payload.code === 'duplicate_schema') return 'Não foi possível gerar um schema único para esta loja.';
    return detail || 'Conflito ao criar a loja (e-mail, domínio ou schema já existente).';
  }
  if (err.status === 400) return detail || 'Dados inválidos. Revise os campos obrigatórios.';
  if (err.status === 500) return 'Erro interno ao criar a loja. Tente novamente ou contate o suporte.';
  if (err.status === 0) return err.message;
  return detail || err.message;
};

const readAuthState = () => {
  try {
    return JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || 'null');
  } catch (e) {
    return null;
  }
};

const writeAuthState = (auth) => {
  if (!auth) {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    __sa_token = '';
    __sa_refresh = '';
    __sa_user = null;
    return;
  }
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
  __sa_token = auth.access || '';
  __sa_refresh = auth.refresh || '';
  __sa_user = auth.user || null;
};

writeAuthState(readAuthState());

const apiClient = {
  async request(method, endpoint, { params = {}, body, auth = true } = {}) {
    const url = new URL(API_BASE_URL + endpoint, window.location.origin);
    Object.entries(params).forEach(([k, v]) => v !== undefined && url.searchParams.set(k, v));
    const headers = { 'Content-Type': 'application/json' };
    if (auth && __sa_token) headers.Authorization = `Bearer ${__sa_token}`;
    let res;
    try {
      res = await fetch(url, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (e) {
      throw new ApiError('Não foi possível conectar ao servidor.', 0, null);
    }
    let payload = null;
    const text = await res.text();
    if (text) {
      try { payload = JSON.parse(text); } catch (e) { payload = { detail: text }; }
    }
    if (!res.ok) {
      const message = payload?.detail || payload?.error || `API ${res.status}: ${res.statusText}`;
      throw new ApiError(message, res.status, payload);
    }
    return payload || {};
  },
  async get(endpoint, params = {}, opts = {}) {
    return this.request('GET', endpoint, { ...opts, params });
  },
  async post(endpoint, body, opts = {}) {
    return this.request('POST', endpoint, { ...opts, body });
  },
  async patch(endpoint, body, opts = {}) {
    return this.request('PATCH', endpoint, { ...opts, body });
  },
  async put(endpoint, body, opts = {}) {
    return this.request('PUT', endpoint, { ...opts, body });
  },
};

let _superAdminConfig = null;
let _superAdminConfigPromise = null;

const DEFAULT_SUPER_ADMIN_CONFIG = {
  environment: 'unknown',
  tenant_domain_suffix: 'localhost',
  tenant_domain_protocol: 'http',
  tenant_domain_template: '{subdomain}.localhost',
  manage_host: 'manage.localhost',
  app_host: 'admin.localhost',
  _fallback: true,
};

/** Normalize ".foo" / "foo" → ".foo". */
function normalizeDomainSuffix(raw) {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  return trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
}

/** Visual-only suffix from Vite env (baked at build). Does not change API payloads. */
function envTenantDomainSuffix() {
  try {
    return normalizeDomainSuffix(import.meta.env?.VITE_TENANT_DOMAIN_SUFFIX);
  } catch {
    return null;
  }
}

/**
 * Visual-only: prefer the Super Admin panel host so manage.localhost (nginx → dist)
 * does not keep a production-baked ".tezus.com.br". Does not change API payloads.
 */
function visualTenantDomainSuffix() {
  if (typeof window !== 'undefined') {
    const host = String(window.location.hostname || '').toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')) {
      return '.localhost';
    }
  }
  const fromEnv = envTenantDomainSuffix();
  if (fromEnv) return fromEnv;
  return null;
}

const configService = {
  async getConfig(force = false) {
    if (!force && _superAdminConfig && !_superAdminConfig._fallback) return _superAdminConfig;
    if (!force && _superAdminConfigPromise) return _superAdminConfigPromise;
    _superAdminConfigPromise = (async () => {
      try {
        const cfg = await apiClient.get('/api/v1/super-admin/config/');
        _superAdminConfig = cfg;
        if (typeof window !== 'undefined') window.__SA_CACHED_CONFIG__ = cfg;
        return cfg;
      } catch (e) {
        _superAdminConfig = { ...DEFAULT_SUPER_ADMIN_CONFIG };
        if (typeof window !== 'undefined') window.__SA_CACHED_CONFIG__ = _superAdminConfig;
        return _superAdminConfig;
      } finally {
        _superAdminConfigPromise = null;
      }
    })();
    return _superAdminConfigPromise;
  },
  getCachedConfig() {
    return _superAdminConfig;
  },
  domainSuffixFromConfig(config) {
    const visual = visualTenantDomainSuffix();
    if (visual) return visual;
    const cfg = config || _superAdminConfig || DEFAULT_SUPER_ADMIN_CONFIG;
    const template = cfg.tenant_domain_template || '{subdomain}.localhost';
    const match = template.match(/^\{subdomain\}\.(.+)$/);
    if (match) return `.${match[1]}`;
    const suffix = cfg.tenant_domain_suffix || 'localhost';
    return suffix.startsWith('.') ? suffix : `.${suffix}`;
  },
  buildDomainFromSubdomain(subdomain, config) {
    const visual = visualTenantDomainSuffix();
    if (visual) return `${subdomain}${visual}`;
    const cfg = config || _superAdminConfig || DEFAULT_SUPER_ADMIN_CONFIG;
    const template = cfg.tenant_domain_template || '{subdomain}.localhost';
    return template.replace('{subdomain}', subdomain);
  },
  buildMockTenantDomain(subdomain, config) {
    return configService.buildDomainFromSubdomain(subdomain, config);
  },
  buildTenantUrl(domain, config) {
    if (!domain) return null;
    const cfg = config || _superAdminConfig || DEFAULT_SUPER_ADMIN_CONFIG;
    const host = String(domain).replace(/^https?:\/\//i, '').split('/')[0];
    const visual = visualTenantDomainSuffix();
    let protocol = cfg.tenant_domain_protocol || 'https';
    if (visual && /localhost$/i.test(visual)) protocol = 'http';
    return `${protocol}://${host}`;
  },
  extractSubdomainFromDomain(domain, config) {
    if (!domain) return '';
    const host = String(domain).replace(/^https?:\/\//i, '').split('/')[0];
    const suffix = configService.domainSuffixFromConfig(config).replace(/^\./, '');
    if (host === suffix) return '';
    const suffixWithDot = `.${suffix}`;
    if (host.endsWith(suffixWithDot)) return host.slice(0, -suffixWithDot.length);
    return host;
  },
};

const authService = {
  getSession() {
    return readAuthState();
  },
  clearSession() {
    writeAuthState(null);
  },
  async login({ email, password }) {
    const response = await apiClient.post('/api/v1/super-admin/auth/login/', { email, password }, { auth: false });
    writeAuthState({
      access: response.access,
      refresh: response.refresh,
      user: response.user,
    });
    return response;
  },
  async me() {
    return apiClient.get('/api/v1/super-admin/auth/me/');
  },
  async refresh() {
    const auth = readAuthState();
    if (!auth?.refresh) throw new ApiError('Sessão expirada.', 401, null);
    const response = await apiClient.post('/api/v1/super-admin/auth/refresh/', { refresh: auth.refresh }, { auth: false });
    writeAuthState({ ...auth, access: response.access });
    return response;
  },
  async logout() {
    try {
      await apiClient.post('/api/v1/super-admin/auth/logout/', {});
    } catch (e) {
      // Stateless logout for MVP: local cleanup is enough.
    } finally {
      writeAuthState(null);
    }
  },
  async bootstrapSession() {
    try {
      const auth = readAuthState();
      if (!auth?.access) {
        writeAuthState(null);
        return { authenticated: false, reason: null };
      }
      try {
        const me = await this.me();
        writeAuthState({ ...auth, user: me.user });
        return { authenticated: true, user: me.user };
      } catch (err) {
        if (err.status === 401 && auth.refresh) {
          try {
            await this.refresh();
            const me = await this.me();
            const updated = readAuthState() || {};
            writeAuthState({ ...updated, user: me.user });
            return { authenticated: true, user: me.user };
          } catch (refreshErr) {
            writeAuthState(null);
            return { authenticated: false, reason: refreshErr.status === 403 ? 'forbidden' : 'unauthorized' };
          }
        }
        if (err.status === 403) {
          writeAuthState(null);
          return { authenticated: false, reason: 'forbidden' };
        }
        if (err.status === 0) {
          writeAuthState(null);
          return { authenticated: false, reason: 'network' };
        }
        writeAuthState(null);
        return { authenticated: false, reason: 'unauthorized' };
      }
    } catch (e) {
      writeAuthState(null);
      return { authenticated: false, reason: 'network' };
    }
  },
};

const DEFAULT_PLAN_CATALOG = {
  basic: { id: 'basic', name: 'Basic', price: 199, pageviews: 50000, users: 10 },
  pro: { id: 'pro', name: 'Pro', price: 499, pageviews: 200000, users: 30 },
};

const mapApiPlanToCatalog = (apiPlan) => {
  const limits = apiPlan.limits || {};
  const fallback = DEFAULT_PLAN_CATALOG[apiPlan.code] || {};
  return {
    id: apiPlan.code,
    name: apiPlan.label || fallback.name || apiPlan.code,
    price: fallback.price || 0,
    pageviews: limits.pageviews ?? fallback.pageviews ?? 0,
    users: limits.internal_users ?? fallback.users ?? 0,
    routesCount: apiPlan.routes_count || (apiPlan.routes || []).length,
    routes: apiPlan.routes || [],
  };
};

const resolvePlan = (planCode) => {
  const plans = PLANS || [];
  const found = plans.find((plan) => plan.id === planCode);
  return found || DEFAULT_PLAN_CATALOG[planCode] || { id: planCode || 'basic', name: planCode || 'basic', price: 0, pageviews: 0, users: 0 };
};

const mapTenantSummaryToStore = (tenant) => {
  const plan = resolvePlan(tenant.plan);
  const commercialStatus = tenant.commercial_status || 'active';
  const uiStatus = commercialStatusToUi(commercialStatus);
  return {
    id: `tenant_${tenant.tenant_id}`,
    tenant_id: tenant.tenant_id,
    schema_name: tenant.schema_name,
    name: tenant.name || tenant.store_settings?.name || `Tenant ${tenant.tenant_id}`,
    domain: tenant.primary_domain || null,
    customDomain: null,
    plan,
    status: uiStatus,
    commercial_status: commercialStatus,
    commercial_status_label: tenant.commercial_status_label || '',
    operational_status: tenant.operational_status || 'active',
    operational_status_label: tenant.operational_status_label || '',
    archived_at: tenant.archived_at || null,
    archive_reason: tenant.archive_reason || null,
    reactivated_at: tenant.reactivated_at || null,
    trial_ends_at: tenant.trial_ends_at || null,
    current_period_ends_at: tenant.current_period_ends_at || null,
    past_due_since: tenant.past_due_since || null,
    suspended_at: tenant.suspended_at || null,
    createdAt: tenant.created ? new Date(tenant.created) : null,
    nextDue: tenant.current_period_ends_at ? new Date(tenant.current_period_ends_at) : null,
    color: '#1B3D60',
    mrr: plan.price || 0,
    ownerName: tenant.owner?.first_name || '',
    ownerEmail: tenant.owner?.email || '',
    ownerPhone: '',
    city: tenant.store_settings?.city || '',
    state: tenant.store_settings?.state || '',
    cnpj: tenant.store_settings?.cnpj || '',
    usage: { pageviews: 0, users: 0 },
    health_summary: tenant.health_summary || {},
  };
};

const mapTenantDetailToStore = (tenant) => {
  const plan = resolvePlan(tenant.plan);
  const primaryDomain = (tenant.domains || []).find((d) => d.is_primary)?.domain || (tenant.domains || [])[0]?.domain || null;
  const commercialStatus = tenant.commercial_status || 'active';
  const uiStatus = commercialStatusToUi(commercialStatus);
  return {
    id: `tenant_${tenant.tenant_id}`,
    tenant_id: tenant.tenant_id,
    schema_name: tenant.schema_name,
    name: tenant.name || tenant.store_settings?.name || `Tenant ${tenant.tenant_id}`,
    fiscal_name: tenant.fiscal_name || '',
    domain: primaryDomain,
    customDomain: null,
    plan,
    status: uiStatus,
    commercial_status: commercialStatus,
    commercial_status_label: tenant.commercial_status_label || '',
    operational_status: tenant.operational_status || 'active',
    operational_status_label: tenant.operational_status_label || '',
    archived_at: tenant.archived_at || null,
    archive_reason: tenant.archive_reason || null,
    reactivated_at: tenant.reactivated_at || null,
    trial_ends_at: tenant.trial_ends_at || null,
    current_period_ends_at: tenant.current_period_ends_at || null,
    past_due_since: tenant.past_due_since || null,
    suspended_at: tenant.suspended_at || null,
    createdAt: tenant.created ? new Date(tenant.created) : null,
    nextDue: tenant.current_period_ends_at ? new Date(tenant.current_period_ends_at) : null,
    color: '#1B3D60',
    mrr: plan.price || 0,
    ownerName: tenant.owner?.first_name || tenant.owner?.email || '',
    ownerEmail: tenant.owner?.email || '',
    ownerPhone: tenant.store_settings?.phone || '',
    city: tenant.store_settings?.city || '',
    state: tenant.store_settings?.state || '',
    cnpj: tenant.store_settings?.cnpj || '',
    complement: tenant.store_settings?.complement || '',
    usage: { pageviews: 0, users: 0 },
    domains: tenant.domains || [],
    store_settings: tenant.store_settings || null,
    owner: tenant.owner || null,
    tenant_mapping: tenant.tenant_mapping || null,
    staff_profile: tenant.staff_profile || null,
    health_summary: tenant.health_summary || {},
  };
};

// ── Plan Service (Fase 4) ─────────────────────────────────────────────────────
const planService = {
  async listPlans() {
    if (USE_REAL_PLANS_API) {
      const response = await apiClient.get('/api/v1/super-admin/plans/');
      const catalog = (response.results || []).map(mapApiPlanToCatalog);
      if (catalog.length) {
        setPLANS(catalog);
      }
      __SA_PERMISSION_SCHEMA_VERSION__ = response.permission_schema_version;
      return response;
    }
    await delay(300);
    return {
      results: (PLANS || Object.values(DEFAULT_PLAN_CATALOG)).map((plan) => ({
        code: plan.id,
        label: plan.name,
        routes_count: 0,
        routes: [],
        limits: { pageviews: plan.pageviews, internal_users: plan.users },
      })),
      permission_schema_version: __SA_PERMISSION_SCHEMA_VERSION__ || '1.31',
    };
  },

  async getPlanRoutes(planCode) {
    if (USE_REAL_PLANS_API) {
      return apiClient.get(`/api/v1/super-admin/plans/${planCode}/routes/`);
    }
    await delay(200);
    return { plan: planCode, routes_count: 0, routes: [] };
  },
};

// ── Tenant Service ─────────────────────────────────────────────────────────────
const tenantService = {
  async list(params = {}) {
    const { _v, ...apiParams } = params;
    if (USE_REAL_TENANTS_API) {
      const response = await apiClient.get('/api/v1/super-admin/tenants/', apiParams);
      const results = response.results || [];
      return {
        data: results.map(mapTenantSummaryToStore),
        total: response.count || 0,
        page: response.page || 1,
        per_page: response.page_size || 20,
        raw: response,
      };
    }
    await delay(500);
    const stores = STORES || [];
    return { data: stores, total: stores.length, page: 1, per_page: 50 };
  },

  async getById(id) {
    if (USE_REAL_TENANTS_API) {
      const response = await apiClient.get(`/api/v1/super-admin/tenants/${id}/`);
      return {
        data: mapTenantDetailToStore(response),
        raw: response,
      };
    }
    await delay(400);
    const stores = STORES || [];
    const store = stores.find(s => s.id === id || String(s.tenant_id) === String(id));
    if (!store) throw new Error('Tenant não encontrado');
    return { data: store, raw: store };
  },

  async updateStatus(tenantId, payload) {
    // payload: { status, reason, notes?, trial_ends_at?, current_period_ends_at? }
    if (USE_REAL_BILLING_API) {
      const commercialStatus = uiStatusToCommercial(payload.status);
      return apiClient.patch(`/api/v1/super-admin/tenants/${tenantId}/billing/status/`, {
        status: commercialStatus,
        reason: payload.reason,
        notes: payload.notes,
        trial_ends_at: payload.trial_ends_at,
        current_period_ends_at: payload.current_period_ends_at,
      });
    }
    await delay(800);
    return {
      tenant_id: tenantId,
      previous_status: payload.previous_status,
      status: uiStatusToCommercial(payload.status),
      message: `Status alterado para ${payload.status}`,
    };
  },

  async archive(tenantId, payload) {
    if (USE_REAL_TENANTS_API) {
      const response = await apiClient.post(`/api/v1/super-admin/tenants/${tenantId}/archive/`, {
        reason: payload.reason,
      });
      return response;
    }
    await delay(600);
    return {
      tenant_id: tenantId,
      previous_status: 'active',
      operational_status: 'archived',
      noop: false,
      reason: payload.reason,
    };
  },

  async reactivate(tenantId, payload) {
    if (USE_REAL_TENANTS_API) {
      const response = await apiClient.post(`/api/v1/super-admin/tenants/${tenantId}/reactivate/`, {
        reason: payload.reason,
      });
      return response;
    }
    await delay(600);
    return {
      tenant_id: tenantId,
      previous_status: 'archived',
      operational_status: 'active',
      noop: false,
      reason: payload.reason,
    };
  },

  async updatePlan(tenantId, payload) {
    // payload: { plan, reason }
    if (USE_REAL_TENANTS_API) {
      return apiClient.patch(`/api/v1/super-admin/tenants/${tenantId}/plan/`, {
        plan: payload.plan,
        reason: payload.reason,
      });
    }
    await delay(800);
    return {
      tenant_id: tenantId,
      plan: payload.plan,
      roles_updated: true,
      message: `Plano alterado para ${payload.plan}`,
    };
  },

  async syncPermissions(tenantId, reason) {
    if (USE_REAL_TENANTS_API) {
      return apiClient.post(`/api/v1/super-admin/tenants/${tenantId}/sync-permissions/`, { reason });
    }
    await delay(800);
    return {
      tenant_id: tenantId,
      routes_synced: true,
      affected_roles: 1,
      added_routes: [],
      removed_routes: [],
    };
  },

  async updatePrimaryDomain(tenantId, payload) {
    if (USE_REAL_TENANTS_API) {
      return apiClient.patch(`/api/v1/super-admin/tenants/${tenantId}/domain/`, payload);
    }
    await delay(600);
    const subdomain = payload.subdomain || '';
    const primary_domain = configService.buildMockTenantDomain(subdomain);
    return {
      tenant_id: tenantId,
      primary_domain,
      url: configService.buildTenantUrl(primary_domain),
      schema_name: 'mock_schema',
    };
  },

  async checkSubdomainAvailability(subdomain) {
    const value = String(subdomain || '').trim().toLowerCase();
    if (USE_REAL_TENANTS_API) {
      return apiClient.get('/api/v1/super-admin/tenants/domain-check/', { subdomain: value });
    }
    await delay(200);
    const domain = configService.buildDomainFromSubdomain(value);
    if (RESERVED_SUBDOMAINS.includes(value)) {
      return { subdomain: value, domain, available: false, reason: 'reserved' };
    }
    const stores = STORES || [];
    const taken = stores.some((s) => {
      const host = String(s.domain || s.customDomain || '').toLowerCase();
      return host === domain.toLowerCase() || host.startsWith(`${value}.`);
    });
    if (taken) {
      return { subdomain: value, domain, available: false, reason: 'taken' };
    }
    return { subdomain: value, domain, available: true, reason: null };
  },

  async updateProfile(tenantId, payload) {
    // payload: { name?, fiscal_name?, store_settings?, reason }
    if (USE_REAL_TENANTS_API) {
      const response = await apiClient.patch(`/api/v1/super-admin/tenants/${tenantId}/`, payload);
      return {
        data: mapTenantDetailToStore(response),
        raw: response,
      };
    }
    await delay(800);
    const stores = STORES || [];
    const store = stores.find((s) => String(s.tenant_id) === String(tenantId));
    if (!store) throw new ApiError('Tenant não encontrado.', 404, null);
    if (payload.name !== undefined) store.name = payload.name;
    if (payload.fiscal_name !== undefined) store.fiscal_name = payload.fiscal_name;
    if (payload.store_settings) {
      store.store_settings = { ...(store.store_settings || {}), ...payload.store_settings };
      if (payload.store_settings.city) store.city = payload.store_settings.city;
      if (payload.store_settings.state) store.state = payload.store_settings.state;
      if (payload.store_settings.cnpj) store.cnpj = payload.store_settings.cnpj;
      if (payload.store_settings.complement !== undefined) store.complement = payload.store_settings.complement;
    }
    return { data: store, raw: store };
  },

  async create(payload) {
    // payload: CreateTenantPayload (name, fiscal_name, email, password?, send_invite_email, domain, plan, store_settings, distribution_center)
    if (USE_REAL_TENANTS_API) {
      // Real backend endpoint (Fase 3): POST /api/v1/super-admin/tenants/
      return apiClient.post('/api/v1/super-admin/tenants/', payload);
    }
    await delay(1500);
    const id = 'stor_' + String(Math.floor(Math.random() * 9000) + 1000);
    const tenantId = Math.floor(Math.random() * 900) + 100;
    const sendInvite = payload.send_invite_email !== false;
    return {
      tenant_id: tenantId,
      schema_name: (payload.subdomain || payload.domain || '').replace(/-/g, '_'),
      domain: payload.domain || configService.buildDomainFromSubdomain(payload.subdomain || ''),
      admin_user: payload.email,
      invite_email_sent: sendInvite,
      invite_email_error: null,
      id,
    };
  },

  async retryProvisioning(tenantId) {
    if (isRealRepairsMode()) {
      return apiClient.post(`/api/v1/super-admin/tenants/${tenantId}/repair/`, { type: 'provisioning' });
    }
    await delay(1200);
    return { success: true, message: 'Re-provisionamento iniciado' };
  },

  async getStatusHistory(tenantId) {
    if (USE_REAL_BILLING_API) {
      const response = await apiClient.get(`/api/v1/super-admin/tenants/${tenantId}/billing/status-history/`);
      return { data: response.results || [] };
    }
    await delay(400);
    const history = STATUS_HISTORY_MOCK || {};
    return { data: history[tenantId] || [] };
  },
};

// ── Billing Service ────────────────────────────────────────────────────────────
const _billingMockPageParams = (params = {}) => {
  let page = Number(params.page);
  let pageSize = Number(params.page_size);
  if (!Number.isFinite(page) || page < 1) page = 1;
  if (!Number.isFinite(pageSize) || pageSize < 1) pageSize = 20;
  if (pageSize > 100) pageSize = 100;
  page = Math.floor(page);
  pageSize = Math.floor(pageSize);
  return { page, page_size: pageSize };
};

const billingService = {
  async getOverview() {
    if (isRealBillingMode()) {
      return apiClient.get('/api/v1/super-admin/billing/overview/');
    }
    await delay(600);
    return BILLING_OVERVIEW_MOCK || {};
  },

  async listTenants(params = {}) {
    if (isRealBillingMode()) {
      return apiClient.get('/api/v1/super-admin/billing/tenants/', params);
    }
    await delay(500);
    const stores = STORES || [];
    return {
      count: stores.length,
      page: 1,
      page_size: 50,
      results: stores.map((s) => ({
        tenant_id: s.tenant_id,
        name: s.name,
        schema_name: s.schema_name,
        plan: s.plan?.id,
        commercial_status: uiStatusToCommercial(s.status),
        commercial_status_label: s.commercial_status_label || '',
        trial_ends_at: s.trial_ends_at || null,
        current_period_ends_at: s.current_period_ends_at || null,
        past_due_since: s.past_due_since || null,
        suspended_at: s.suspended_at || null,
        updated_at: null,
      })),
    };
  },

  async getTenantBilling(tenantId) {
    if (isRealBillingMode()) {
      return apiClient.get(`/api/v1/super-admin/tenants/${tenantId}/billing/`);
    }
    await delay(300);
    return { tenant_id: tenantId, commercial_status: 'active' };
  },

  async updateTenantBillingStatus(tenantId, payload) {
    if (isRealBillingMode()) {
      return apiClient.patch(`/api/v1/super-admin/tenants/${tenantId}/billing/status/`, payload);
    }
    await delay(700);
    return { tenant_id: tenantId, ...payload };
  },

  async getTenantStatusHistory(tenantId) {
    if (isRealBillingMode()) {
      return apiClient.get(`/api/v1/super-admin/tenants/${tenantId}/billing/status-history/`);
    }
    await delay(400);
    return { tenant_id: tenantId, results: (STATUS_HISTORY_MOCK || {})[tenantId] || [] };
  },

  async listGlobalSubscriptions(params = {}) {
    if (isRealBillingMode()) {
      return apiClient.get('/api/v1/super-admin/billing/subscriptions/', params);
    }
    await delay(500);
    const source = SUBSCRIPTIONS_MOCK || [];
    const { page, page_size: pageSize } = _billingMockPageParams(params);
    const start = (page - 1) * pageSize;
    const slice = source.slice(start, start + pageSize);
    const mapFn = typeof saBillingMapGlobalSubscription === 'function'
      ? saBillingMapGlobalSubscription
      : (item) => item;
    return {
      count: source.length,
      page,
      page_size: pageSize,
      results: slice.map(mapFn),
    };
  },

  async getTenantActiveSubscription(tenantId) {
    if (isRealBillingMode()) {
      return apiClient.get(`/api/v1/super-admin/tenants/${tenantId}/billing/subscription/`);
    }
    throw new ApiError(
      'Esta operação de assinatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async createTenantSubscription(tenantId, payload) {
    if (isRealBillingMode()) {
      return apiClient.post(`/api/v1/super-admin/tenants/${tenantId}/billing/subscriptions/`, payload);
    }
    throw new ApiError(
      'Esta operação de assinatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async getTenantSubscriptionDetail(tenantId, subscriptionId) {
    if (isRealBillingMode()) {
      return apiClient.get(
        `/api/v1/super-admin/tenants/${tenantId}/billing/subscriptions/${subscriptionId}/`,
      );
    }
    throw new ApiError(
      'Esta operação de assinatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async updateTenantSubscription(tenantId, subscriptionId, payload) {
    if (isRealBillingMode()) {
      return apiClient.patch(
        `/api/v1/super-admin/tenants/${tenantId}/billing/subscriptions/${subscriptionId}/`,
        payload,
      );
    }
    throw new ApiError(
      'Esta operação de assinatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async endTenantSubscription(tenantId, subscriptionId, payload) {
    if (isRealBillingMode()) {
      return apiClient.post(
        `/api/v1/super-admin/tenants/${tenantId}/billing/subscriptions/${subscriptionId}/end/`,
        payload,
      );
    }
    throw new ApiError(
      'Esta operação de assinatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async cancelTenantSubscription(tenantId, subscriptionId, payload) {
    if (isRealBillingMode()) {
      return apiClient.post(
        `/api/v1/super-admin/tenants/${tenantId}/billing/subscriptions/${subscriptionId}/cancel/`,
        payload,
      );
    }
    throw new ApiError(
      'Esta operação de assinatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async getTenantSubscriptionHistory(tenantId, subscriptionId, params = {}) {
    if (isRealBillingMode()) {
      return apiClient.get(
        `/api/v1/super-admin/tenants/${tenantId}/billing/subscriptions/${subscriptionId}/history/`,
        params,
      );
    }
    throw new ApiError(
      'Esta operação de assinatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async listGlobalInvoices(params = {}) {
    if (isRealBillingMode()) {
      return apiClient.get('/api/v1/super-admin/billing/invoices/', params);
    }
    await delay(500);
    const source = INVOICES_MOCK || [];
    const { page, page_size: pageSize } = _billingMockPageParams(params);
    const start = (page - 1) * pageSize;
    const slice = source.slice(start, start + pageSize);
    const mapFn = typeof saBillingMapGlobalInvoice === 'function'
      ? saBillingMapGlobalInvoice
      : (item) => item;
    return {
      count: source.length,
      page,
      page_size: pageSize,
      results: slice.map(mapFn),
    };
  },

  async listTenantInvoices(tenantId, params = {}) {
    if (isRealBillingMode()) {
      return apiClient.get(
        `/api/v1/super-admin/tenants/${tenantId}/billing/invoices/`,
        params,
      );
    }
    throw new ApiError(
      'Esta operação de fatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async createTenantInvoice(tenantId, payload) {
    if (isRealBillingMode()) {
      return apiClient.post(
        `/api/v1/super-admin/tenants/${tenantId}/billing/invoices/`,
        payload,
      );
    }
    throw new ApiError(
      'Esta operação de fatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async getTenantInvoiceDetail(tenantId, invoiceId) {
    if (isRealBillingMode()) {
      return apiClient.get(
        `/api/v1/super-admin/tenants/${tenantId}/billing/invoices/${invoiceId}/`,
      );
    }
    throw new ApiError(
      'Esta operação de fatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async updateTenantInvoice(tenantId, invoiceId, payload) {
    if (isRealBillingMode()) {
      return apiClient.patch(
        `/api/v1/super-admin/tenants/${tenantId}/billing/invoices/${invoiceId}/`,
        payload,
      );
    }
    throw new ApiError(
      'Esta operação de fatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async markTenantInvoicePaid(tenantId, invoiceId, payload) {
    if (isRealBillingMode()) {
      return apiClient.post(
        `/api/v1/super-admin/tenants/${tenantId}/billing/invoices/${invoiceId}/mark-paid/`,
        payload,
      );
    }
    throw new ApiError(
      'Esta operação de fatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async cancelTenantInvoice(tenantId, invoiceId, payload) {
    if (isRealBillingMode()) {
      return apiClient.post(
        `/api/v1/super-admin/tenants/${tenantId}/billing/invoices/${invoiceId}/cancel/`,
        payload,
      );
    }
    throw new ApiError(
      'Esta operação de fatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async getTenantInvoiceHistory(tenantId, invoiceId, params = {}) {
    if (isRealBillingMode()) {
      return apiClient.get(
        `/api/v1/super-admin/tenants/${tenantId}/billing/invoices/${invoiceId}/history/`,
        params,
      );
    }
    throw new ApiError(
      'Esta operação de fatura está disponível apenas no Billing real.',
      501,
      { code: 'billing_action_not_supported' },
    );
  },

  async getSubscriptions(params = {}) {
    const response = await billingService.listGlobalSubscriptions(params);
    return {
      ...response,
      data: response.results || [],
      total: response.count || 0,
    };
  },

  async getInvoices(params = {}) {
    const response = await billingService.listGlobalInvoices(params);
    return {
      ...response,
      data: response.results || [],
      total: response.count || 0,
    };
  },

  async markInvoicePaid(invoiceId, { reason }) {
    if (isRealBillingMode()) {
      throw new ApiError(
        'A marcação de fatura como paga exige contexto de tenant e será integrada na fase da lista de faturas.',
        501,
        { code: 'billing_action_not_supported' },
      );
    }
    await delay(700);
    return { invoice_id: invoiceId, status: 'paid', message: 'Fatura marcada como paga' };
  },

  async retryPayment(invoiceId) {
    if (isRealBillingMode()) {
      throw new ApiError(
        'Esta ação não está disponível no Billing administrativo atual.',
        501,
        { code: 'billing_action_not_supported' },
      );
    }
    await delay(900);
    const ok = Math.random() > 0.4;
    if (!ok) throw new Error('Cobrança rejeitada pelo gateway');
    return { invoice_id: invoiceId, status: 'paid', message: 'Cobrança processada com sucesso' };
  },

  async sendInvoice(invoiceId) {
    if (isRealBillingMode()) {
      throw new ApiError(
        'Esta ação não está disponível no Billing administrativo atual.',
        501,
        { code: 'billing_action_not_supported' },
      );
    }
    await delay(600);
    return { success: true, message: 'Fatura enviada por e-mail' };
  },

  async cancelInvoice(invoiceId, { reason }) {
    if (isRealBillingMode()) {
      throw new ApiError(
        'O cancelamento de fatura exige contexto de tenant e será integrado na fase da lista de faturas.',
        501,
        { code: 'billing_action_not_supported' },
      );
    }
    await delay(700);
    return { invoice_id: invoiceId, status: 'cancelled', message: 'Fatura cancelada' };
  },

  async getDunningRules() {
    if (isRealBillingMode()) {
      throw new ApiError(
        'Esta ação não está disponível no Billing administrativo atual.',
        501,
        { code: 'billing_action_not_supported' },
      );
    }
    await delay(400);
    return { data: DUNNING_RULES_MOCK || [] };
  },

  async getFinancialHistory() {
    if (isRealBillingMode()) {
      throw new ApiError(
        'Esta ação não está disponível no Billing administrativo atual.',
        501,
        { code: 'billing_action_not_supported' },
      );
    }
    await delay(400);
    return { data: FINANCIAL_HISTORY_MOCK || [] };
  },
};

const MVP_INTERNAL_ROLES = [
  { id: 'role_owner', code: 'owner', name: 'Owner', description: 'Acesso total à plataforma (metadado MVP)', is_system: true, permissions: ['*'], users: 0, status: 'active' },
  { id: 'role_admin', code: 'admin', name: 'Admin', description: 'Administrador geral', is_system: true, permissions: [], users: 0, status: 'active' },
  { id: 'role_operations', code: 'operations', name: 'Operações', description: 'Operações e provisionamento', is_system: false, permissions: [], users: 0, status: 'active' },
  { id: 'role_finance', code: 'finance', name: 'Financeiro', description: 'Billing e status comercial', is_system: false, permissions: [], users: 0, status: 'active' },
  { id: 'role_support_readonly', code: 'support_readonly', name: 'Suporte leitura', description: 'Somente leitura operacional', is_system: false, permissions: [], users: 0, status: 'active' },
];

const formatInternalUserError = (err) => {
  if (!(err instanceof ApiError)) return err?.message || 'Erro ao processar usuário interno.';
  const payload = err.payload || {};
  const detail = payload.detail || payload.error || err.message;
  if (err.status === 409 && payload.code === 'duplicate_email') return 'Já existe um usuário com este e-mail.';
  if (err.status === 403) return detail || 'Acesso negado. Sua conta pode estar bloqueada.';
  if (err.status === 400) return detail || 'Dados inválidos.';
  if (err.status === 0) return err.message;
  return detail || err.message;
};

const formatRelativeTime = (value) => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 2) return 'Agora';
  if (mins < 60) return `Há ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `Há ${days} dia${days > 1 ? 's' : ''}`;
  return d.toLocaleDateString('pt-BR');
};

const mapAuthRoleToDrawerLabel = (role, isPlatformRoot) => {
  if (isPlatformRoot || role === 'owner') return 'Super admin';
  if (role === 'admin') return 'Admin';
  const labels = {
    operations: 'Operações',
    finance: 'Financeiro',
    support_readonly: 'Suporte leitura',
  };
  return labels[role] || role || 'Não disponível';
};

/** Real display name from auth/me — never falls back to email. */
const resolveAuthUserRealName = (apiUser) => {
  if (!apiUser) return '';
  const fromParts = `${apiUser.first_name || ''} ${apiUser.last_name || ''}`.trim();
  if (fromParts) return fromParts;
  return (apiUser.name || '').trim();
};

/** Label for titles/cards: real name when available, otherwise email. */
const resolveAuthUserDisplayLabel = (apiUser) => {
  if (!apiUser) return '';
  return resolveAuthUserRealName(apiUser) || apiUser.email || '';
};

const resolveAuthUserInitials = (apiUser) => {
  const realName = resolveAuthUserRealName(apiUser);
  if (realName) {
    const parts = realName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  const email = apiUser?.email || '';
  const local = email.split('@')[0] || '';
  return (local.slice(0, 2) || '??').toUpperCase();
};

/** Maps GET /auth/me user payload to the shape expected by UserEditorDrawer (profile view). */
const mapAuthUserToProfileDrawer = (apiUser) => {
  if (!apiUser) return null;
  const realName = resolveAuthUserRealName(apiUser);
  return {
    id: apiUser.id,
    name: realName,
    displayName: resolveAuthUserDisplayLabel(apiUser),
    hasRealName: !!realName,
    first_name: apiUser.first_name || '',
    last_name: apiUser.last_name || '',
    email: apiUser.email || '',
    role: mapAuthRoleToDrawerLabel(apiUser.role, apiUser.is_platform_root),
    phone: '',
    status: apiUser.is_blocked ? 'inativo' : 'ativo',
    is_blocked: !!apiUser.is_blocked,
    fromAuth: true,
  };
};

/** Sidebar user card from auth/me session user. */
const mapAuthUserToSidebarCard = (apiUser) => {
  if (!apiUser) return null;
  return {
    label: resolveAuthUserDisplayLabel(apiUser),
    role: mapAuthRoleToDrawerLabel(apiUser.role, apiUser.is_platform_root),
    initials: resolveAuthUserInitials(apiUser),
  };
};

const mapApiInternalUserToUi = (row) => {
  const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email;
  let status = 'active';
  if (row.is_blocked) status = 'blocked';
  else if (!row.is_active) status = 'inactive';
  else if (row.status === 'pending') status = 'pending';

  return {
    id: row.id,
    name,
    email: row.email,
    first_name: row.first_name || '',
    last_name: row.last_name || '',
    role: row.role_label || row.role,
    role_code: row.role,
    status,
    is_blocked: !!row.is_blocked,
    is_active: row.is_active !== false,
    mfa_enabled: false,
    last_seen: formatRelativeTime(row.last_seen_at || row.last_login),
    last_login: row.last_login,
    created_at: row.created_at ? new Date(row.created_at).toLocaleDateString('pt-BR') : '—',
    notes: row.notes || '',
  };
};

const mapFormToCreatePayload = (form) => {
  const parts = (form.name || '').trim().split(/\s+/).filter(Boolean);
  const first_name = form.first_name || parts[0] || '';
  const last_name = form.last_name || parts.slice(1).join(' ') || '';
  return {
    email: form.email,
    first_name,
    last_name,
    role: form.role_code,
    password: form.password || undefined,
    notes: form.notes || '',
    send_invite: !!form.send_invite,
  };
};

// ── Internal users (Fase 7) ───────────────────────────────────────────────────
const internalUserService = {
  async listUsers(params = {}) {
    if (USE_REAL_INTERNAL_USERS_API) {
      const response = await apiClient.get('/api/v1/super-admin/users/', params);
      const results = (response.results || []).map(mapApiInternalUserToUi);
      return { data: results, count: response.count, page: response.page, page_size: response.page_size };
    }
    await delay(400);
    const data = (INTERNAL_USERS_EXT || []).map(mapApiInternalUserToUi);
    return { data, count: data.length };
  },

  async getUser(id) {
    if (USE_REAL_INTERNAL_USERS_API) {
      const row = await apiClient.get(`/api/v1/super-admin/users/${id}/`);
      return mapApiInternalUserToUi(row);
    }
    await delay(300);
    const found = (INTERNAL_USERS_EXT || []).find(u => String(u.id) === String(id));
    return found ? mapApiInternalUserToUi(found) : null;
  },

  async createUser(payload) {
    if (USE_REAL_INTERNAL_USERS_API) {
      const row = await apiClient.post('/api/v1/super-admin/users/', payload);
      return mapApiInternalUserToUi(row);
    }
    await delay(800);
    return mapApiInternalUserToUi({ id: 'iu_' + Date.now(), ...payload, status: 'pending', created_at: new Date().toISOString(), role_label: payload.role });
  },

  async updateUser(id, payload) {
    if (USE_REAL_INTERNAL_USERS_API) {
      const row = await apiClient.patch(`/api/v1/super-admin/users/${id}/`, payload);
      return mapApiInternalUserToUi(row);
    }
    await delay(600);
    return mapApiInternalUserToUi({ id, ...payload });
  },

  async blockUser(id, reason) {
    if (USE_REAL_INTERNAL_USERS_API) {
      const row = await apiClient.post(`/api/v1/super-admin/users/${id}/block/`, { reason });
      return mapApiInternalUserToUi(row);
    }
    await delay(600);
    return mapApiInternalUserToUi({ id, status: 'blocked', is_blocked: true });
  },

  async unblockUser(id, reason) {
    if (USE_REAL_INTERNAL_USERS_API) {
      const row = await apiClient.post(`/api/v1/super-admin/users/${id}/unblock/`, { reason });
      return mapApiInternalUserToUi(row);
    }
    await delay(600);
    return mapApiInternalUserToUi({ id, status: 'active', is_blocked: false });
  },

  getMvpRoles() {
    return MVP_INTERNAL_ROLES;
  },
};

const superAdminUserService = {
  async list(params) {
    return internalUserService.listUsers(params);
  },

  async create(payload) {
    const body = USE_REAL_INTERNAL_USERS_API ? (payload.email ? payload : mapFormToCreatePayload(payload)) : payload;
    return internalUserService.createUser(body);
  },

  async update(userId, payload) {
    return internalUserService.updateUser(userId, payload);
  },

  async block(userId, { reason }) {
    return internalUserService.blockUser(userId, reason);
  },

  async unblock(userId, { reason } = {}) {
    return internalUserService.unblockUser(userId, reason || 'Desbloqueio autorizado');
  },

  async revokeSessions(userId) {
    if (!USE_MOCK_API) {
      // Future backend endpoint suggested in technical spec.
      // POST /api/v1/super-admin/users/:user_id/revoke-sessions/
      return apiClient.post(`/api/v1/super-admin/users/${userId}/revoke-sessions/`);
    }
    await delay(700);
    return { success: true, message: 'Sessões revogadas' };
  },

  async invite(userId) {
    if (!USE_MOCK_API) {
      // Future backend endpoint suggested in technical spec.
      // POST /api/v1/super-admin/users/:user_id/invite/
      return apiClient.post(`/api/v1/super-admin/users/${userId}/invite/`);
    }
    await delay(600);
    return { success: true, message: 'Convite reenviado' };
  },

  async getRoles() {
    if (!USE_MOCK_API) {
      // Future backend endpoint suggested in technical spec.
      // GET /api/v1/super-admin/roles/
      return apiClient.get('/api/v1/super-admin/roles/');
    }
    await delay(400);
    const data = SA_ROLES_MOCK || [];
    return { data, total: data.length };
  },

  async getSessions() {
    if (!USE_MOCK_API) {
      // Future backend endpoint suggested in technical spec.
      // GET /api/v1/super-admin/sessions/
      return apiClient.get('/api/v1/super-admin/sessions/');
    }
    await delay(400);
    return { data: SESSIONS_MOCK || [] };
  },
};

const mapApiNotificationToUi = (row) => ({
  id: row.id,
  type: row.type,
  severity: row.severity,
  title: row.title,
  body: row.body,
  tenant: row.tenant?.name || row.tenant_name || null,
  tenant_id: row.tenant?.id ?? row.tenant_id ?? null,
  created_at: typeof formatAuditTimestamp === 'function'
    ? formatAuditTimestamp(row.created_at)
    : row.created_at,
  read_at: row.read_at
    ? (typeof formatAuditTimestamp === 'function' ? formatAuditTimestamp(row.read_at) : row.read_at)
    : null,
  resolved_at: row.resolved_at,
  status: row.resolved_at ? 'resolved' : 'open',
  payload: row.payload || {},
  source_action: row.source_action,
  _read: !!row.read_at,
});

// ── Notification Service ───────────────────────────────────────────────────────
const notificationService = {
  async list(params = {}) {
    if (USE_REAL_NOTIFICATIONS_API) {
      const response = await apiClient.get('/api/v1/super-admin/notifications/', params);
      const results = (response.results || []).map(mapApiNotificationToUi);
      return {
        data: results,
        total: response.count ?? results.length,
        unread_count: response.unread_count ?? 0,
        count: response.count ?? results.length,
      };
    }
    await delay(400);
    const data = OPERATIONAL_NOTIFICATIONS_MOCK || [];
    return { data, total: data.length, unread_count: data.filter(n => !n.read_at).length };
  },

  async markRead(notifId) {
    if (USE_REAL_NOTIFICATIONS_API) {
      return apiClient.post(`/api/v1/super-admin/notifications/${notifId}/read/`);
    }
    await delay(300);
    return { success: true };
  },

  async markAllRead() {
    if (USE_REAL_NOTIFICATIONS_API) {
      return apiClient.post('/api/v1/super-admin/notifications/read-all/');
    }
    await delay(400);
    return { success: true };
  },

  async resolve(notifId, { comment } = {}) {
    if (USE_REAL_NOTIFICATIONS_API) {
      return apiClient.post(`/api/v1/super-admin/notifications/${notifId}/resolve/`, { comment });
    }
    await delay(500);
    return { success: true };
  },

  async getAlertRules() {
    if (!USE_MOCK_API) {
      // Future backend endpoint suggested in technical spec.
      // GET /api/v1/super-admin/alert-rules/
      return apiClient.get('/api/v1/super-admin/alert-rules/');
    }
    await delay(400);
    return { data: ALERT_RULES_MOCK || [] };
  },

  async createAlertRule(payload) {
    if (!USE_MOCK_API) {
      // Future backend endpoint suggested in technical spec.
      // POST /api/v1/super-admin/alert-rules/
      return apiClient.post('/api/v1/super-admin/alert-rules/', payload);
    }
    await delay(700);
    return { id: 'ar_' + Date.now(), ...payload };
  },
};

// ── Audit Service ──────────────────────────────────────────────────────────────
const AUDIT_ACTION_OPTIONS = [
  { value: 'all', label: 'Todas as ações' },
  { value: 'tenant.created', label: 'Loja criada' },
  { value: 'tenant.creation_failed', label: 'Falha ao criar loja' },
  { value: 'tenant.domain_updated', label: 'Domínio atualizado' },
  { value: 'tenant.domain_update_failed', label: 'Falha ao atualizar domínio' },
  { value: 'tenant.plan_changed', label: 'Plano alterado' },
  { value: 'tenant.plan_change_failed', label: 'Falha ao alterar plano' },
  { value: 'tenant.permissions_synced', label: 'Permissões sincronizadas' },
  { value: 'tenant.permissions_sync_failed', label: 'Falha ao sincronizar permissões' },
  { value: 'tenant.archived', label: 'Loja arquivada (operacional)' },
  { value: 'tenant.reactivated', label: 'Loja reativada (operacional)' },
  { value: 'tenant.archive_failed', label: 'Falha ao arquivar loja' },
  { value: 'tenant.reactivate_failed', label: 'Falha ao reativar loja' },
  { value: 'billing.status_changed', label: 'Status comercial alterado' },
  { value: 'billing.status_change_failed', label: 'Falha ao alterar status comercial' },
];

const severityApiToUi = (severity) => {
  const map = {
    info: 'info',
    warning: 'atencao',
    critical: 'critico',
    error: 'critico',
  };
  return map[severity] || 'info';
};

const severityUiToApi = (severity) => {
  const map = {
    info: 'info',
    atencao: 'warning',
    critico: 'critical',
    seguranca: 'warning',
  };
  return map[severity] || severity;
};

const resultApiToUi = (result) => (result === 'failed' ? 'falha' : result);

const resultUiToApi = (result) => (result === 'falha' ? 'failed' : result);

const formatAuditTimestamp = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();
  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `Hoje, ${time}`;
  if (isYesterday) return `Ontem, ${time}`;
  return date.toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const periodToDateFrom = (period) => {
  if (!period || period === 'all') return undefined;
  const now = new Date();
  const from = new Date(now);
  if (period === '24h') from.setHours(now.getHours() - 24);
  else if (period === '7d') from.setDate(now.getDate() - 7);
  else if (period === '30d') from.setDate(now.getDate() - 30);
  else return undefined;
  return from.toISOString();
};

const mapAuditLogToEvent = (log) => {
  const metadata = log.metadata || {};
  const before = {};
  const after = {};
  if (metadata.previous_plan || metadata.new_plan) {
    if (metadata.previous_plan) before.plan = metadata.previous_plan;
    if (metadata.new_plan) after.plan = metadata.new_plan;
  }
  if (metadata.previous_domain || metadata.new_domain) {
    if (metadata.previous_domain) before.domain = metadata.previous_domain;
    if (metadata.new_domain) after.domain = metadata.new_domain;
  }
  if (metadata.previous_status || metadata.new_status) {
    if (metadata.previous_status) before.status = metadata.previous_status;
    if (metadata.new_status) after.status = metadata.new_status;
  }
  const resource = log.target_type && log.target_id
    ? `${log.target_type} #${log.target_id}`
    : (log.action || '—');
  return {
    id: log.id,
    ts: formatAuditTimestamp(log.created_at),
    createdAt: log.created_at,
    actor: log.actor?.name || log.actor?.email || 'Sistema',
    actorEmail: log.actor?.email || '',
    actorType: 'Super admin',
    tenant: log.tenant?.name || '—',
    tenantId: log.tenant?.id || null,
    action: log.action_label || log.action,
    actionCode: log.action,
    resource,
    result: resultApiToUi(log.result),
    severity: severityApiToUi(log.severity),
    ip: log.ip_address || '—',
    reason: log.reason || '—',
    before: Object.keys(before).length ? before : null,
    after: Object.keys(after).length ? after : null,
    metadata,
    raw: log,
  };
};

const auditService = {
  async listLogs(params = {}) {
    if (USE_REAL_AUDIT_API) {
      const query = {
        page: params.page || 1,
        page_size: params.page_size || 20,
        search: params.search || undefined,
        action: params.action || undefined,
        actor_id: params.actor_id || undefined,
        actor_email: params.actor_email || undefined,
        tenant_id: params.tenant_id || undefined,
        target_type: params.target_type || undefined,
        target_id: params.target_id || undefined,
        result: params.result || undefined,
        severity: params.severity || undefined,
        date_from: params.date_from || undefined,
        date_to: params.date_to || undefined,
      };
      const response = await apiClient.get('/api/v1/super-admin/audit-logs/', query);
      const results = (response.results || []).map(mapAuditLogToEvent);
      return {
        data: results,
        count: response.count || 0,
        page: response.page || 1,
        page_size: response.page_size || 20,
        raw: response,
      };
    }
    await delay(400);
    const data = (AUDIT || []).map((entry, index) => ({
      id: entry.id || `mock_${index}`,
      ts: entry.when || '—',
      createdAt: entry.timestamp || null,
      actor: entry.user || '—',
      actorEmail: '',
      actorType: 'Super admin',
      tenant: entry.storeName || '—',
      tenantId: entry.storeId || null,
      action: entry.action || '—',
      actionCode: entry.action || '',
      resource: '—',
      result: entry.kind === 'danger' ? 'falha' : 'success',
      severity: entry.kind === 'danger' ? 'critico' : 'info',
      ip: entry.ip || '—',
      reason: '—',
      before: null,
      after: null,
      metadata: {},
      raw: entry,
    }));
    return { data, count: data.length, page: 1, page_size: data.length, raw: { results: data } };
  },

  async getLog(id) {
    if (USE_REAL_AUDIT_API) {
      const response = await apiClient.get(`/api/v1/super-admin/audit-logs/${id}/`);
      return mapAuditLogToEvent(response);
    }
    await delay(300);
    const entry = (AUDIT || []).find((item, index) => String(item.id || index) === String(id));
    if (!entry) throw new ApiError('Log não encontrado.', 404, null);
    return mapAuditLogToEvent({
      id,
      action: entry.action,
      action_label: entry.action,
      actor: { name: entry.user, email: entry.user },
      tenant: entry.storeId ? { id: entry.storeId, name: entry.storeName } : null,
      target_type: 'tenant',
      target_id: entry.storeId ? String(entry.storeId) : '',
      result: entry.kind === 'danger' ? 'failed' : 'success',
      severity: entry.kind === 'danger' ? 'error' : 'info',
      reason: '',
      metadata: {},
      ip_address: entry.ip,
      created_at: entry.timestamp,
    });
  },

  async export(params = {}) {
    if (!USE_MOCK_API && USE_REAL_AUDIT_API) {
      return apiClient.get('/api/v1/super-admin/audit-logs/export/', params);
    }
    await delay(800);
    return { url: '#', filename: `auditoria-${Date.now()}.csv` };
  },
};

// ── Repairs Service (Fase 8) ───────────────────────────────────────────────────
const formatRepairError = (err) => {
  if (!(err instanceof ApiError)) return err?.message || 'Erro ao executar repair.';
  const payload = err.payload || {};
  return payload.detail || payload.error || err.message;
};

const repairService = {
  async getTenantHealth(tenantId) {
    if (isRealRepairsMode()) {
      return apiClient.get(`/api/v1/super-admin/tenants/${tenantId}/health/`);
    }
    await delay(300);
    return {
      checks: [],
      available_repairs: [],
      schema_accessible: true,
      _mock: true,
      _status: 'coming_soon',
      message: 'Health checks ainda não disponíveis no backend.',
    };
  },

  async runRepair(tenantId, { action, reason } = {}) {
    if (isRealRepairsMode()) {
      const body = { action };
      if (reason) body.reason = reason;
      return apiClient.post(`/api/v1/super-admin/tenants/${tenantId}/repair/`, body);
    }
    await delay(400);
    return {
      status: 'not_available',
      message: 'Repairs ainda não disponíveis — funcionalidade em preparação.',
      action,
      _mock: true,
    };
  },

  async getRepairHistory(tenantId) {
    if (isRealRepairsMode()) {
      const response = await apiClient.get(`/api/v1/super-admin/tenants/${tenantId}/repairs/history/`);
      return response.results || [];
    }
    await delay(200);
    return [];
  },

  async getOverview(params = {}) {
    if (isRealRepairsMode()) {
      return apiClient.get('/api/v1/super-admin/repairs/overview/', params);
    }
    await delay(200);
    return {
      totals: { executed: 0, success: 0, failed: 0, not_available: 0 },
      by_action: [],
      recent_window: { days: 30 },
      updated_at: new Date().toISOString(),
      _mock: true,
      _status: 'coming_soon',
    };
  },
};

const createApiNotifStore = () => {
  let items = [];
  let unreadCount = 0;
  let loading = false;
  let loadError = null;
  const listeners = [];
  const notify = () => listeners.forEach(fn => fn([...items]));

  const reload = async () => {
    if (!USE_REAL_NOTIFICATIONS_API) return;
    loading = true;
    loadError = null;
    try {
      const response = await notificationService.list({ page: 1, page_size: 100, status: 'all' });
      items = response.data || [];
      unreadCount = response.unread_count ?? items.filter(n => !n._read && !n.read_at).length;
    } catch (e) {
      loadError = e?.message || 'Erro ao carregar notificações';
      items = [];
      unreadCount = 0;
    } finally {
      loading = false;
      notify();
    }
  };

  reload();

  return {
    getAll: () => [...items],
    getUnreadCount: () => unreadCount,
    hasSLAAlert: () => false,
    getLoadError: () => loadError,
    isLoading: () => loading,
    markRead: async (id) => {
      try {
        await notificationService.markRead(id);
        const n = items.find(x => x.id === id);
        if (n) {
          const wasUnread = !n._read && !n.read_at;
          n._read = true;
          n.read_at = n.read_at || new Date().toISOString();
          if (wasUnread) unreadCount = Math.max(0, unreadCount - 1);
        }
        notify();
      } catch (e) {
        throw e;
      }
    },
    markAllRead: async () => {
      await notificationService.markAllRead();
      items.forEach(n => { n._read = true; n.read_at = n.read_at || new Date().toISOString(); });
      unreadCount = 0;
      notify();
    },
    resolve: async (id) => {
      await notificationService.resolve(id);
      const n = items.find(x => x.id === id);
      if (n) {
        const wasUnread = !n._read && !n.read_at;
        n.status = 'resolved';
        n._read = true;
        n.read_at = n.read_at || new Date().toISOString();
        if (wasUnread) unreadCount = Math.max(0, unreadCount - 1);
      }
      notify();
    },
    subscribe: (fn) => {
      listeners.push(fn);
      return () => { const i = listeners.indexOf(fn); if (i >= 0) listeners.splice(i, 1); };
    },
    reload,
  };
};

/** Single reactive store for bell + inbox page. Real API or in-memory mock — never window. */
export let _notifStore = USE_REAL_NOTIFICATIONS_API
  ? createApiNotifStore()
  : _mockNotifStore;


// ── Impersonation Service ──────────────────────────────────────────────────────
const impersonationService = {
  async start({ tenantId, userId, reason, readonly = false }) {
    if (!USE_MOCK_API) {
      // Future backend endpoint suggested in technical spec.
      // POST /api/v1/super-admin/impersonation/start/
      return apiClient.post('/api/v1/super-admin/impersonation/start/', {
        tenant_id: tenantId, target_user_id: userId, reason, readonly,
      });
    }
    await delay(900);
    return {
      session_id: 'imp_' + Date.now(),
      token: 'mock_imp_token_' + Date.now(),
      expires_at: new Date(Date.now() + 3600000).toISOString(),
      readonly,
    };
  },

  async end(sessionId) {
    if (!USE_MOCK_API) {
      // Future backend endpoint suggested in technical spec.
      // POST /api/v1/super-admin/impersonation/:session_id/end/
      return apiClient.post(`/api/v1/super-admin/impersonation/${sessionId}/end/`);
    }
    await delay(400);
    return { success: true };
  },
};

// ── Hooks ──────────────────────────────────────────────────────────────────────

function useTenants(initialParams = {}) {
  const [data,    setData]    = React.useState(null);
  const [meta,    setMeta]    = React.useState({ total: 0, page: 1, per_page: 20 });
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState(null);
  const [params,  setParams]  = React.useState(initialParams);

  const fetchData = React.useCallback(async (nextParams = params) => {
    setLoading(true); setError(null);
    try {
      const r = await tenantService.list(nextParams);
      setData(r.data);
      setMeta({ total: r.total, page: r.page, per_page: r.per_page });
      setParams(nextParams);
    }
    catch (e) { setError(e.message); }
    finally   { setLoading(false); }
  }, [params]);

  React.useEffect(() => { fetchData(initialParams); }, [JSON.stringify(initialParams)]);
  return { data, meta, loading, error, refetch: fetchData, params };
}

function useCreateTenant() {
  const [loading, setLoading] = React.useState(false);
  const [error,   setError]   = React.useState(null);
  const [result,  setResult]  = React.useState(null);

  const create = React.useCallback(async (payload) => {
    setLoading(true); setError(null);
    try {
      const r = await tenantService.create(payload);
      setResult(r);
      return r;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = React.useCallback(() => { setError(null); setResult(null); }, []);
  return { create, loading, error, result, reset };
}

function useUpdateTenantPlan() {
  const [loading, setLoading] = React.useState(false);
  const [error,   setError]   = React.useState(null);

  const update = React.useCallback(async (tenantId, payload) => {
    // payload: { plan, reason }
    // Real endpoint: PUT /api/v1/tenants/:tenant_id/plan/
    setLoading(true); setError(null);
    try {
      return await tenantService.updatePlan(tenantId, payload);
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { update, loading, error };
}

function useUpdateTenantStatus() {
  const [loading, setLoading] = React.useState(false);
  const [error,   setError]   = React.useState(null);

  const update = React.useCallback(async (tenantId, payload) => {
    // payload: { status, reason, notify_owner }
    // Future endpoint: PATCH /api/v1/super-admin/tenants/:tenant_id/status/
    setLoading(true); setError(null);
    try {
      return await tenantService.updateStatus(tenantId, payload);
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { update, loading, error };
}

function useBillingOverview() {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState(null);
  const requestIdRef = React.useRef(0);

  const fetchData = React.useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await billingService.getOverview();
      if (requestId !== requestIdRef.current) {
        return { ok: false, stale: true };
      }
      setData(result);
      return {
        ok: true,
        stale: false,
        data: result,
      };
    } catch (e) {
      if (requestId !== requestIdRef.current) {
        return { ok: false, stale: true };
      }
      const fallback = 'Não foi possível carregar a visão geral de Billing.';
      const message = typeof saBillingFormatError === 'function'
        ? saBillingFormatError(e, fallback)
        : (e && e.message) || fallback;
      setError(message);
      return {
        ok: false,
        stale: false,
        error: e,
        message,
      };
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  React.useEffect(() => { fetchData(); }, [fetchData]);
  return { data, loading, error, refetch: fetchData };
}

function useGlobalBillingSubscriptions(params = {}) {
  const [data, setData] = React.useState([]);
  const [meta, setMeta] = React.useState({ count: 0, page: 1, page_size: 20, total_pages: 1 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const requestIdRef = React.useRef(0);
  const paramsKey = JSON.stringify(params || {});

  const fetchData = React.useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const parsedParams = JSON.parse(paramsKey);
      const result = await billingService.listGlobalSubscriptions(parsedParams);
      if (requestId !== requestIdRef.current) {
        return { ok: false, stale: true };
      }
      const mapFn = typeof saBillingMapGlobalSubscription === 'function'
        ? saBillingMapGlobalSubscription
        : (item) => item;
      const results = Array.isArray(result && result.results) ? result.results : [];
      const count = Number(result && result.count) || 0;
      const page = Number(result && result.page) || 1;
      const pageSize = Number(result && result.page_size) || 20;
      setData(results.map(mapFn));
      setMeta({
        count,
        page,
        page_size: pageSize,
        total_pages: Math.max(1, Math.ceil(count / pageSize)),
      });
      return {
        ok: true,
        stale: false,
        data: result,
      };
    } catch (e) {
      if (requestId !== requestIdRef.current) {
        return { ok: false, stale: true };
      }
      const fallback = 'Não foi possível carregar as assinaturas.';
      const message = typeof saBillingFormatError === 'function'
        ? saBillingFormatError(e, fallback)
        : (e && e.message) || fallback;
      setError(message);
      return {
        ok: false,
        stale: false,
        error: e,
        message,
      };
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [paramsKey]);

  React.useEffect(() => { fetchData(); }, [fetchData]);
  return { data, meta, loading, error, refetch: fetchData };
}

function useGlobalBillingInvoices(params = {}) {
  const [data, setData] = React.useState([]);
  const [meta, setMeta] = React.useState({ count: 0, page: 1, page_size: 20, total_pages: 1 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const requestIdRef = React.useRef(0);
  const paramsKey = JSON.stringify(params || {});

  const fetchData = React.useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const parsedParams = JSON.parse(paramsKey);
      const result = await billingService.listGlobalInvoices(parsedParams);
      if (requestId !== requestIdRef.current) {
        return { ok: false, stale: true };
      }
      const mapFn = typeof saBillingMapGlobalInvoice === 'function'
        ? saBillingMapGlobalInvoice
        : (item) => item;
      const results = Array.isArray(result && result.results) ? result.results : [];
      const count = Number(result && result.count) || 0;
      const page = Number(result && result.page) || 1;
      const pageSize = Number(result && result.page_size) || 20;
      setData(results.map(mapFn));
      setMeta({
        count,
        page,
        page_size: pageSize,
        total_pages: Math.max(1, Math.ceil(count / pageSize)),
      });
      return {
        ok: true,
        stale: false,
        data: result,
      };
    } catch (e) {
      if (requestId !== requestIdRef.current) {
        return { ok: false, stale: true };
      }
      const fallback = 'Não foi possível carregar as faturas.';
      const message = typeof saBillingFormatError === 'function'
        ? saBillingFormatError(e, fallback)
        : (e && e.message) || fallback;
      setError(message);
      return {
        ok: false,
        stale: false,
        error: e,
        message,
      };
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [paramsKey]);

  React.useEffect(() => { fetchData(); }, [fetchData]);
  return { data, meta, loading, error, refetch: fetchData };
}

function useTenantInvoices() {
  const [data,    setData]    = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState(null);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try   { const r = await billingService.getInvoices(); setData(r.data); }
    catch (e) { setError(e.message); }
    finally   { setLoading(false); }
  }, []);

  React.useEffect(() => { fetchData(); }, []);
  return { data, loading, error, refetch: fetchData };
}

function useOperationalNotifications() {
  const [data,    setData]    = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState(null);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try   { const r = await notificationService.list(); setData(r.data); }
    catch (e) { setError(e.message); }
    finally   { setLoading(false); }
  }, []);

  React.useEffect(() => { fetchData(); }, []);
  return { data, loading, error, refetch: fetchData };
}

function useSuperAdminUsers(filters = {}) {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const [error,   setError]   = React.useState(null);
  const hasLoadedRef = React.useRef(false);
  const latestRequestRef = React.useRef(0);

  const fetchData = React.useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    const hadData = hasLoadedRef.current;
    if (!hadData) setLoading(true);
    else setIsRefetching(true);
    setError(null);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      const r = await internalUserService.listUsers(params);
      if (requestId !== latestRequestRef.current) return;
      setData(r.data);
      hasLoadedRef.current = true;
    } catch (e) {
      if (requestId !== latestRequestRef.current) return;
      setError(formatInternalUserError(e));
      if (!hadData) {
        setData([]);
        hasLoadedRef.current = true;
      }
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setIsRefetching(false);
      }
    }
  }, [filters.search, filters.role, filters.status]);

  React.useEffect(() => {
    fetchData();
    return () => { latestRequestRef.current += 1; };
  }, [fetchData]);

  return {
    data: data ?? [],
    loading: loading && data == null,
    error,
    refetch: fetchData,
    isRefetching,
  };
}

// ── Loading / Error / Empty UI states ─────────────────────────────────────────
const SkeletonRow = ({ cols = 5 }) => (
  <tr style={{ borderTop: '1px solid var(--border)' }}>
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} style={{ padding: '12px 16px' }}>
        <div style={{ height: 14, borderRadius: 6, background: 'var(--border)', animation: 'pulse 1.5s ease-in-out infinite', width: i === 0 ? '70%' : i === cols - 1 ? '40%' : '60%' }} />
      </td>
    ))}
  </tr>
);

const SkeletonTable = ({ rows = 5, cols = 5 }) => (
  <>{Array.from({ length: rows }).map((_, i) => <SkeletonRow key={i} cols={cols} />)}</>
);

const ApiErrorState = ({ error, onRetry }) => (
  <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
    <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--danger-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
      <Icon name="alert" size={22} style={{ color: 'var(--danger)' }} />
    </div>
    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Erro ao carregar</div>
    <div style={{ fontSize: 13, marginBottom: 16 }}>{error || 'Não foi possível conectar ao servidor.'}</div>
    {onRetry && <button className="btn" onClick={onRetry}><Icon name="refresh" size={13} />Tentar novamente</button>}
  </div>
);

const EmptyState = ({ icon = 'search', title = 'Nenhum resultado', description = '', action }) => (
  <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
    <Icon name={icon} size={40} style={{ display: 'block', margin: '0 auto 12px', opacity: 0.3 }} />
    <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>{title}</div>
    {description && <div style={{ fontSize: 13, marginBottom: 16 }}>{description}</div>}
    {action && <button className="btn" onClick={action.onClick}>{action.label}</button>}
  </div>
);

// ── SensitiveActionModal — modal padrão para ações com motivo obrigatório ──────
const SensitiveActionModal = ({
  open, onClose, onConfirm,
  title, description, warning,
  confirmLabel = 'Confirmar',
  danger = false,
  requireReason = true,
  requireConfirmCheck = false,
}) => {
  const [reason,  setReason]  = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [saving,  setSaving]  = React.useState(false);

  React.useEffect(() => {
    if (!open) { setReason(''); setChecked(false); setSaving(false); }
  }, [open]);

  const canSubmit = (!requireReason || reason.trim().length >= 5) && (!requireConfirmCheck || checked) && !saving;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try { await onConfirm({ reason, checked }); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 480, background: 'var(--bg-elevated)', borderRadius: 16, boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
        <div style={{ padding: '24px 24px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: danger ? 'var(--danger-bg)' : 'var(--info-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name={danger ? 'alert' : 'check'} size={20} style={{ color: danger ? 'var(--danger)' : 'var(--info)' }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>
              {description && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{description}</div>}
            </div>
          </div>
          {warning && (
            <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--warning)', marginBottom: 16, lineHeight: 1.5 }}>
              ⚠ {warning}
            </div>
          )}
          {requireReason && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Motivo <span style={{ color: 'var(--danger)' }}>*</span></label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Descreva o motivo desta ação (mínimo 5 caracteres)..." rows={3}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 }} />
            </div>
          )}
          {requireConfirmCheck && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 8 }}>
              <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ width: 14, height: 14, marginTop: 2, accentColor: 'var(--primary)' }} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Entendo o impacto desta ação. Esta ação será registrada na auditoria.</span>
            </label>
          )}
          {!requireConfirmCheck && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
              🔒 Esta ação é registrada na auditoria do Super Admin.
            </div>
          )}
        </div>
        <div style={{ padding: '12px 24px', background: 'var(--bg-subtle)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className={'btn ' + (danger ? 'danger' : 'primary')} onClick={handleConfirm} disabled={!canSubmit}>
            {saving
              ? <><Icon name="refresh" size={13} style={{ animation: 'spin 1s linear infinite' }} />Salvando…</>
              : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export {
    API_BASE_URL,
    AUDIT_ACTION_OPTIONS,
  AUTH_STORAGE_KEY,
  ApiError,
  ApiErrorState,
  EmptyState,
  MVP_INTERNAL_ROLES,
  SensitiveActionModal,
  SkeletonRow,
  SkeletonTable,
  USE_MOCK_API,
  USE_REAL_AUDIT_API,
  USE_REAL_BILLING_API,
  USE_REAL_INTERNAL_USERS_API,
  USE_REAL_NOTIFICATIONS_API,
  USE_REAL_PLANS_API,
  USE_REAL_REPAIRS_API,
  USE_REAL_TENANTS_API,
  apiClient,
  auditService,
  authService,
  billingService,
  buildTenantProfileUpdatePayload,
  commercialStatusToUi,
  configService,
  delay,
  formatAuditTimestamp,
  formatBillingStatusError,
  formatInternalUserError,
  formatOperationalStatusError,
  formatPlanUpdateError,
  formatRepairError,
  formatTenantCreateError,
  formatTenantDomainUpdateError,
  formatTenantProfileUpdateError,
  impersonationService,
  internalUserService,
  isRealBillingMode,
  isRealRepairsMode,
  isRealTenantsMode,
  mapApiInternalUserToUi,
  mapAuditLogToEvent,
  mapAuthUserToProfileDrawer,
  mapAuthUserToSidebarCard,
  mapFormToCreatePayload,
  normalizeStoreSettingsForProfile,
  notificationService,
  periodToDateFrom,
  planService,
  readAuthState,
  repairService,
  resolveAuthUserDisplayLabel,
  resolveAuthUserInitials,
  resolveAuthUserRealName,
  resolvePlan,
  resultApiToUi,
  resultUiToApi,
  severityApiToUi,
  severityUiToApi,
  shouldUseMockBilling,
  shouldUseMockRepairs,
  shouldUseMockTenants,
  superAdminUserService,
  tenantService,
  uiStatusToCommercial,
  useBillingOverview,
  useCreateTenant,
  useGlobalBillingInvoices,
  useGlobalBillingSubscriptions,
  useOperationalNotifications,
  useSuperAdminUsers,
  useTenantInvoices,
  useTenants,
  useUpdateTenantPlan,
  useUpdateTenantStatus,
  writeAuthState,
};
